import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsers } from '../../redux/slices/userSlice';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Plus, Search, Filter, X, Edit2, Trash2, Calendar, Users, User,
  DollarSign, CheckCircle, Clock, AlertCircle, Video, CreditCard,
  BookOpen, GraduationCap, TrendingUp, Send, ChevronRight, ChevronLeft, Download
} from 'lucide-react';
import {
  getAllCourses, addCourse, updateCourse, deleteCourse,
  getAllEnrollments, addEnrollment, updateEnrollment, deleteEnrollment,
  getAllClasses, addClass, updateClass, deleteClass,
  markAttendance, getCompletedStudents, getEnrollmentStats,
  recordPayment, getPendingPayments, getOverduePayments, getPaymentStats,
  getAttendanceByDate
} from '../../services/classService';
import { getAllStaff } from '../../services/staffService';
import CustomDatePicker from '../../components/crm/CustomDatePicker';

const ClassManagement = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.user);
  
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [completedStudents, setCompletedStudents] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, dropped: 0 });
  const [paymentStats, setPaymentStats] = useState({ totalPending: 0, overdueCount: 0 });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [courseAttendance, setCourseAttendance] = useState({});
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberFilterStatus, setMemberFilterStatus] = useState('active');
  const [attendanceFilter, setAttendanceFilter] = useState('all');
  const [sponsorFilter, setSponsorFilter] = useState('all');
  const [selectedMemberDetails, setSelectedMemberDetails] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);

  useEffect(() => {
    if (selectedCourseDetails && attendanceDate) {
      loadCourseAttendance(attendanceDate);
    }
  }, [selectedCourseDetails, attendanceDate]);

  const loadCourseAttendance = async (date) => {
    try {
      const records = await getAttendanceByDate(date);
      const attendanceMap = {};
      records.forEach(r => {
        attendanceMap[r.enrollmentId] = r.status;
      });
      setCourseAttendance(attendanceMap);
    } catch (error) {
      console.error('Error loading attendance', error);
    }
  };

  const exportMembers = () => {
    if (!selectedCourseDetails) return;
    const courseMembers = enrollments.filter(e => e.courseId === selectedCourseDetails.id);
    const headers = ['Member Name', 'Mobile', 'Status', 'Payment Status', 'Total Paid', 'Balance', 'Attended Days', 'Absent Days'];
    const csvContent = [
      headers.join(','),
      ...courseMembers.map(m => 
        `"${m.name || m.studentName || ''}","${m.mobile || ''}","${m.status || 'active'}","${m.paymentStatus || 'pending'}","${m.amountPaid || 0}","${m.balance || 0}","${m.attendedDays || 0}","${m.absentDays || 0}"`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedCourseDetails.name.replace(/\s+/g, '_')}_Members.csv`;
    link.click();
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesData, enrollmentsData, classesData, staffData, statsData, paymentStatsData] = await Promise.all([
        getAllCourses(),
        getAllEnrollments(),
        getAllClasses(),
        getAllStaff(),
        getEnrollmentStats(),
        getPaymentStats()
      ]);

      setCourses(coursesData);
      setEnrollments(enrollmentsData);
      setClasses(classesData);
      setStaff(staffData);
      setStats(statsData);
      setPaymentStats(paymentStatsData);
    } catch (error) {  
      toast.error('Failed to load data');
      console.error(error);
    }
  };

  const handleAddCourse = async (courseData) => {
    try {
      await addCourse(courseData);
      toast.success('Course added successfully');
      loadData();
      setShowCourseModal(false);
    } catch (error) {
      toast.error('Failed to add course');
    }
  };

  const handleUpdateCourse = async (courseData) => {
    try {
      await updateCourse(editingItem.id, courseData);
      toast.success('Course updated successfully');
      
      if (selectedCourseDetails && selectedCourseDetails.id === editingItem.id) {
         setSelectedCourseDetails({ ...editingItem, ...courseData });
      }
      
      loadData();
      setShowCourseModal(false);
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const handleDeleteCourse = async () => {
    if (!deleteItem) return;
    try {
      await deleteCourse(deleteItem.id);
      toast.success('Course deleted successfully');
      setSelectedCourseDetails(null);
      loadData();
      setDeleteItem(null);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleRecordPayment = async (paymentDataArray) => {
    try {
      let currentAmountPaid = parseFloat(selectedMemberDetails?.amountPaid || 0);
      let currentTotalAmount = parseFloat(selectedMemberDetails?.totalAmount || 0);
      let currentHistory = selectedMemberDetails?.paymentHistory ? [...selectedMemberDetails.paymentHistory] : [];

      for (const payment of paymentDataArray) {
         if (!payment.amount || parseFloat(payment.amount) <= 0) continue;
         await recordPayment(selectedEnrollment.id, payment);
         currentAmountPaid += parseFloat(payment.amount);
         currentHistory.push(payment);
      }

      toast.success('Payments recorded successfully');
      
      if (selectedMemberDetails && selectedMemberDetails.id === selectedEnrollment.id) {
         setSelectedMemberDetails(prev => {
            const newBalance = (currentTotalAmount - currentAmountPaid).toString();
            const newPaymentStatus = parseFloat(newBalance) <= 0 ? 'paid' : 'partial';
            return {
               ...prev,
               amountPaid: currentAmountPaid.toString(),
               balance: newBalance,
               paymentStatus: newPaymentStatus,
               paymentHistory: currentHistory
            };
         });
      }
      
      loadData();
      setShowPaymentModal(false);
    } catch (error) {
      toast.error('Failed to record payments');
    }
  };

  const handleBulkRemove = async () => {
    if (selectedMembers.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove ${selectedMembers.length} member(s) from this course?`)) return;
    
    try {
      await Promise.all(selectedMembers.map(id => deleteEnrollment(id)));
      toast.success(`${selectedMembers.length} member(s) removed successfully`);
      setSelectedMembers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to remove members');
    }
  };

  const toggleSelectAll = (e, membersList) => {
    if (e.target.checked) {
      setSelectedMembers(membersList.map(m => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const toggleMemberSelection = (id) => {
    setSelectedMembers(prev => 
      prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
    );
  };

  const filteredEnrollments = enrollments.filter(e => {
    const matchesSearch = e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.mobile?.includes(searchTerm) ||
      e.courseName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || e.status === filterStatus;
    const matchesPayment = filterPaymentStatus === 'all' || e.paymentStatus === filterPaymentStatus;
    return matchesSearch && matchesStatus && matchesPayment;
  });

  const courseViewMembers = selectedCourseDetails ? enrollments
    .filter(e => e.courseId === selectedCourseDetails.id)
    .filter(e => {
        const matchesSearch = (e.name || e.studentName || '').toLowerCase().includes(memberSearchTerm.toLowerCase()) || (e.mobile || '').includes(memberSearchTerm);
        const matchesStatus = memberFilterStatus === 'all' || e.status === memberFilterStatus;
        
        let matchesAttendance = true;
        if (attendanceFilter !== 'all') {
           const status = courseAttendance[e.id];
           if (attendanceFilter === 'present') matchesAttendance = status === 'present';
           else if (attendanceFilter === 'absent') matchesAttendance = status === 'absent';
           else if (attendanceFilter === 'unmarked') matchesAttendance = !status;
        }

        let matchesSponsor = true;
        if (sponsorFilter !== 'all') {
           matchesSponsor = e.sponsoredBy === sponsorFilter;
        }
        
        return matchesSearch && matchesStatus && matchesAttendance && matchesSponsor;
    }).sort((a, b) => {
        if (memberFilterStatus === 'completed' || memberFilterStatus === 'dropped') {
            const dateA = new Date(a.courseEndDate?.seconds ? a.courseEndDate.seconds * 1000 : a.courseEndDate || a.updatedAt?.seconds * 1000 || a.updatedAt || 0);
            const dateB = new Date(b.courseEndDate?.seconds ? b.courseEndDate.seconds * 1000 : b.courseEndDate || b.updatedAt?.seconds * 1000 || b.updatedAt || 0);
            return dateB - dateA; // Newest removed first
        }
        return 0;
    }) : [];

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen pb-52">
      {/* Header */}
      <div className="mb-6">
        <div className='flex items-center justify-between'>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Class Management</h1>
            <p className="text-gray-600">Manage courses, enrollments, classes, and payments</p>
          </div>
          <div className="flex justify-end mb-4">
              <button onClick={() => setShowCourseModal(true)} className="bg-primary text-white flex items-center text-sm font-semibold px-4 py-2 rounded-md">
                <Plus className="w-4 h-4" /> Add Course
              </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} title="Total Enrollments" value={stats.total} color="blue" />
        <StatCard icon={TrendingUp} title="Active Members" value={stats.active} color="green" />
        <StatCard icon={GraduationCap} title="Completed" value={stats.completed} color="purple" />
        <StatCard icon={DollarSign} title="Pending Payments" value={`₹${paymentStats.totalPending.toLocaleString()}`} color="orange" />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 flex overflow-hidden">
        <button onClick={() => { setActiveTab('courses'); setSelectedCourseDetails(null); setSelectedMemberDetails(null); }} className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'courses' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
          Classes
        </button>
        <button onClick={() => { setActiveTab('members'); setSelectedCourseDetails(null); setSelectedMemberDetails(null); }} className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'members' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
          All Members
        </button>
      </div>

      {selectedMemberDetails ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-4 md:p-6 mb-8 relative animate-fadeIn">
          <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shadow-sm"><User className="w-5 h-5" /></div>
                {selectedMemberDetails.name || selectedMemberDetails.studentName}
              </h2>
              <div className="text-sm font-semibold text-gray-500 mt-2 flex gap-4">
                 <span>📱 {selectedMemberDetails.mobile}</span>
                 {selectedMemberDetails.gender && <span className="capitalize text-gray-400">👤 {selectedMemberDetails.gender}</span>}
              </div>
            </div>
            <button onClick={() => setSelectedMemberDetails(null)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center shrink-0 transition-colors">
              <X className="w-4 h-4 mr-1" /> Close
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-bold text-gray-900 mb-4 border-b pb-2 flex items-center"><BookOpen className="w-4 h-4 mr-2 text-blue-500" /> Enrollment Information</h4>
              <p className="text-sm mb-3"><span className="text-gray-400 font-bold uppercase w-24 inline-block text-[10px]">Course</span> <span className="font-bold text-gray-800">{selectedMemberDetails.courseName}</span></p>
              <p className="text-sm mb-3"><span className="text-gray-400 font-bold uppercase w-24 inline-block text-[10px]">Status</span> <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md tracking-wider ${selectedMemberDetails.status === 'completed' ? 'bg-green-100 text-green-700' : selectedMemberDetails.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{selectedMemberDetails.status}</span></p>
              <p className="text-sm"><span className="text-gray-400 font-bold uppercase w-24 inline-block text-[10px]">Start Date</span> <span className="font-semibold text-gray-700">{selectedMemberDetails.startDate || 'N/A'}</span></p>
            </div>
            
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-bold text-gray-900 mb-4 border-b pb-2 flex items-center"><Calendar className="w-4 h-4 mr-2 text-purple-500" /> Attendance Overview</h4>
              <div className="flex gap-4 mb-3">
                 <div className="bg-green-50 text-green-800 py-3 rounded-xl flex-1 text-center border border-green-100"><span className="block text-3xl font-black">{selectedMemberDetails.attendedDays || 0}</span> <span className="text-[10px] uppercase font-bold text-green-600">Attended</span></div>
                 <div className="bg-red-50 text-red-800 py-3 rounded-xl flex-1 text-center border border-red-100"><span className="block text-3xl font-black">{selectedMemberDetails.absentDays || 0}</span> <span className="text-[10px] uppercase font-bold text-red-600">Absent</span></div>
              </div>
              <p className="text-[11px] font-bold text-center text-gray-400 uppercase mt-2 border-t pt-2 border-gray-50"><span className="text-gray-700">{selectedMemberDetails.completedClasses || 0}</span> of <span className="text-gray-700">{selectedMemberDetails.totalClasses || 0}</span> Classes logged</p>
            </div>
            
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                 <h4 className="font-bold text-gray-900 flex items-center"><DollarSign className="w-4 h-4 mr-2 text-orange-500" /> Payment Details</h4>
                 <button onClick={() => { setSelectedEnrollment(selectedMemberDetails); setShowPaymentModal(true); }} className="text-xs font-bold bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors flex items-center"><CreditCard className="w-3.5 h-3.5 mr-1" /> Update Payment</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                 <div className="bg-gray-50 p-3 rounded-xl border border-gray-100"><span className="text-[10px] text-gray-500 uppercase font-black block mb-1">Total Fee</span><span className="font-black text-lg text-gray-900">₹{parseFloat(selectedMemberDetails.totalAmount || 0).toLocaleString()}</span></div>
                 <div className="bg-green-50 p-3 rounded-xl border border-green-100/50"><span className="text-[10px] text-green-600 uppercase font-black block mb-1">Paid</span><span className="font-black text-lg text-green-700">₹{parseFloat(selectedMemberDetails.amountPaid || 0).toLocaleString()}</span></div>
                 <div className="bg-red-50 p-3 rounded-xl border border-red-100/50"><span className="text-[10px] text-red-600 uppercase font-black block mb-1">Balance</span><span className="font-black text-lg text-red-700">₹{parseFloat(selectedMemberDetails.balance || 0).toLocaleString()}</span></div>
                 <div className="bg-blue-50 p-3 rounded-xl border border-blue-100/50"><span className="text-[10px] text-blue-600 uppercase font-black block mb-1">Status</span><span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md tracking-wider inline-block ${selectedMemberDetails.paymentStatus === 'paid' ? 'bg-green-200 text-green-800' : selectedMemberDetails.paymentStatus === 'partial' ? 'bg-orange-200 text-orange-800' : 'bg-red-200 text-red-800'}`}>{selectedMemberDetails.paymentStatus}</span></div>
              </div>
              {selectedMemberDetails.paymentHistory && selectedMemberDetails.paymentHistory.length > 0 ? (
                 <div>
                   <h5 className="text-[11px] font-black uppercase text-gray-400 mb-3 ml-1">Payment History</h5>
                   <div className="space-y-2">
                      {selectedMemberDetails.paymentHistory.map((ph, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm bg-white p-3 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600"><CheckCircle className="w-4 h-4" /></div>
                            <div>
                              <span className="font-bold text-gray-900 text-base">₹{parseFloat(ph.amount).toLocaleString()}</span>
                              <span className="text-[10px] font-bold uppercase text-gray-400 ml-2 bg-gray-100 border px-2 py-0.5 rounded-md">{ph.paymentMethod}</span>
                            </div>
                          </div>
                          <div className="text-xs font-semibold text-gray-400">{ph.paymentDate ? format(new Date(ph.paymentDate), 'MMM dd, yyyy') : ''}</div>
                        </div>
                      ))}
                   </div>
                 </div>
              ) : (
                 <div className="text-center py-6 text-gray-400 text-sm font-semibold border-2 border-dashed rounded-xl">No payment history found</div>
              )}
            </div>
          </div>
        </div>
      ) : selectedCourseDetails ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-4 md:p-6 mb-8">
          {/* Details header */}
          <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
            <div>
              <button onClick={() => setSelectedCourseDetails(null)} className="text-gray-500 hover:text-gray-900 flex items-center mb-3 text-sm font-medium transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Classes
              </button>
              <h2 className="text-2xl font-bold">{selectedCourseDetails.name} <span className="text-sm font-normal text-gray-500 ml-2 bg-gray-100 px-2 py-1 rounded">ID: {selectedCourseDetails.classId || selectedCourseDetails.id.slice(0, 6)}</span></h2>
              <div className="flex flex-wrap items-center text-sm text-gray-600 mt-3 gap-y-2">
                {selectedCourseDetails.startDate && <span className="flex items-center mr-4"><Calendar className="w-4 h-4 mr-1 text-gray-400" /> {selectedCourseDetails.startDate} {selectedCourseDetails.endDate ? `to ${selectedCourseDetails.endDate}` : ''}</span>}
                {selectedCourseDetails.totalHours && <span className="flex items-center mr-4"><Clock className="w-4 h-4 mr-1 text-gray-400" /> {selectedCourseDetails.totalHours} Hours</span>}
                {selectedCourseDetails.meetingLink && (
                  <a href={selectedCourseDetails.meetingLink} target="_blank" rel="noreferrer" className="flex items-center text-blue-600 hover:underline"><Video className="w-4 h-4 mr-1" /> Join Meeting</a>
                )}
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row items-end md:items-center gap-2">
              <button onClick={() => { setEditingItem(selectedCourseDetails); setShowCourseModal(true); }} className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-semibold transition-colors flex items-center shrink-0 w-full md:w-auto justify-center">
                <Edit2 className="w-4 h-4 mr-1.5" /> Edit
              </button>
              <button onClick={() => { setDeleteItem(selectedCourseDetails); setShowDeleteModal(true); }} className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-semibold transition-colors flex items-center shrink-0 mr-0 md:mr-2 w-full md:w-auto justify-center">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </button>
              <button onClick={() => setSelectedCourseDetails(null)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center shrink-0 transition-colors w-full md:w-auto justify-center">
                <X className="w-4 h-4 mr-1" /> Close
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-blue-50 p-4 rounded-xl mb-6 gap-4">
             <div className="flex justify-between items-center w-full md:w-auto shrink-0 gap-3">
               <div>
                  <h3 className="font-bold text-blue-900 flex items-center"><Users className="w-5 h-5 mr-2" /> Enrolled Members</h3>
                  <p className="text-[10px] md:text-xs text-blue-700 mt-0.5">Manage attendance</p>
               </div>
               <div className="flex gap-2">
                 {selectedMembers.length > 0 && (
                   <button onClick={handleBulkRemove} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold shadow flex items-center transition-colors">
                     <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove ({selectedMembers.length})
                   </button>
                 )}
                 <button onClick={() => setShowEnrollmentModal(true)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow flex items-center transition-colors">
                   <Plus className="w-3.5 h-3.5 mr-1" /> Add
                 </button>
               </div>
             </div>
             
             <div className="grid grid-cols-2 md:flex flex-wrap items-center gap-3 w-full md:w-auto">
               <div className="relative col-span-2 md:col-span-1">
                 <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                 <input type="text" placeholder="Search..." value={memberSearchTerm} onChange={(e) => setMemberSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 w-full md:w-40 outline-none" />
               </div>
               <select value={memberFilterStatus} onChange={(e) => setMemberFilterStatus(e.target.value)} className="col-span-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                 <option value="all">All Status</option>
                 <option value="active">Active Members</option>
                 <option value="completed">Completed / Removed</option>
                 <option value="dropped">Dropped</option>
               </select>
               <select value={attendanceFilter} onChange={(e) => setAttendanceFilter(e.target.value)} className="col-span-2 md:col-span-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                 <option value="all">All Attendance</option>
                 <option value="present">Present</option>
                 <option value="absent">Absent</option>
                 <option value="unmarked">Not Marked</option>
               </select>
               <select value={sponsorFilter} onChange={(e) => setSponsorFilter(e.target.value)} className="col-span-2 md:col-span-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white max-w-[140px]">
                 <option value="all">All Sponsors</option>
                 {Array.from(new Set(enrollments.filter(e => e.courseId === selectedCourseDetails?.id).map(e => e.sponsoredBy).filter(Boolean))).sort().map(sponsor => (
                   <option key={sponsor} value={sponsor}>{sponsor}</option>
                 ))}
               </select>
               <button onClick={exportMembers} className="col-span-1 px-3 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold flex items-center justify-center transition-colors shadow-sm">
                 <Download className="w-4 h-4 mr-1.5 text-blue-600" /> Export
               </button>
               <div className="col-span-2 flex items-center bg-white border border-blue-200 rounded-lg overflow-hidden shadow-sm shrink-0">
                 <span className="px-3 py-2 bg-blue-100/50 text-xs font-semibold text-blue-800 border-r border-blue-100 flex-none">Date:</span>
                 <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} className="px-3 py-2 text-sm text-gray-800 outline-none border-none flex-1 min-w-0" />
               </div>
             </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {courseViewMembers.length === 0 ? (
              <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-200">No members found.</div>
            ) : courseViewMembers.map(enr => {
              const currentStatus = courseAttendance[enr.id];
              return (
                <div key={enr.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm relative">
                  <div className="flex justify-between items-start mb-3 border-b border-gray-100 pb-3">
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={selectedMembers.includes(enr.id)} onChange={() => toggleMemberSelection(enr.id)} className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                      <div className="pr-2 cursor-pointer group" onClick={() => setSelectedMemberDetails(enr)}>
                        <div className="font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors inline-block border-b border-transparent group-hover:border-blue-200">{enr.name || enr.studentName || 'Member'}</div>
                        <div className="text-[11px] font-semibold text-gray-500 mt-1">📱 {enr.mobile}</div>
                        {enr.sponsoredBy && <div className="text-[10px] text-gray-400 mt-0.5 uppercase font-semibold">Sponsor: <span className="text-gray-600">{enr.sponsoredBy}</span></div>}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-[9px] font-bold uppercase rounded-md tracking-wider shrink-0 break-keep ${enr.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' : enr.status === 'active' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>{enr.status || 'active'}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                      <div className="text-[9px] text-gray-400 font-bold uppercase mb-1.5 flex items-center"><DollarSign className="w-3 h-3 mr-0.5" /> Payment</div>
                      <span className={`px-2 py-1 text-[9px] font-bold uppercase rounded-md tracking-wider inline-block mb-1 ${enr.paymentStatus === 'paid' ? 'bg-green-100 text-green-700 border border-green-200' : enr.paymentStatus === 'partial' ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>{enr.paymentStatus || 'pending'}</span>
                      <div className="text-[10px] font-semibold text-gray-600 block mt-0.5">Bal: <span className={parseFloat(enr.balance || 0) > 0 ? "text-red-600 font-bold" : "text-green-600 font-bold"}>₹{parseFloat(enr.balance || 0).toLocaleString()}</span></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                      <div className="text-[9px] text-gray-400 font-bold uppercase mb-1.5 flex items-center"><Calendar className="w-3 h-3 mr-0.5" /> Attendance</div>
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-700 font-bold flex items-center"><span className="text-green-600 bg-green-100 rounded-sm w-4 h-4 flex items-center justify-center mr-1.5">✓</span> {enr.attendedDays || 0} Attended</span>
                        <span className="text-[10px] text-gray-700 font-bold flex items-center"><span className="text-red-500 bg-red-100 rounded-sm w-4 h-4 flex items-center justify-center mr-1.5">✗</span> {enr.absentDays || 0} Absent</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    {enr.status === 'completed' || enr.status === 'dropped' ? (
                       <div className="flex-1 flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                         <span className="text-[10px] text-gray-500 font-bold uppercase">Date Removed:</span>
                         <span className="text-xs font-bold text-gray-800">
                           {enr.courseEndDate ? (enr.courseEndDate.seconds ? format(new Date(enr.courseEndDate.seconds * 1000), 'MMM dd, yyyy') : format(new Date(enr.courseEndDate), 'MMM dd, yyyy')) : 'Unknown'}
                         </span>
                       </div>
                    ) : (
                      <>
                        <div className="text-[10px] text-gray-500 font-bold uppercase">Set Attendance</div>
                        {currentStatus ? (
                           <span className={`px-3 py-1.5 text-[10px] font-black uppercase rounded-lg border shadow-sm inline-block ${currentStatus === 'present' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                             {currentStatus === 'present' ? 'Attended' : 'Absent'}
                           </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={async () => {
                              try { setCourseAttendance(prev => ({...prev, [enr.id]: 'present'})); await markAttendance({ enrollmentId: enr.id, classDate: attendanceDate, status: 'present' }); toast.success('Marked Attended'); loadData(); } catch (e) { toast.error('Error marking attendance'); setCourseAttendance(prev => ({...prev, [enr.id]: undefined})); }
                            }} className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 hover:bg-green-600 hover:text-white rounded-lg transition-colors text-xs font-bold flex items-center shadow-sm"><CheckCircle className="w-3.5 h-3.5 mr-1" /> Present</button>
                            <button onClick={async () => {
                              try { setCourseAttendance(prev => ({...prev, [enr.id]: 'absent'})); await markAttendance({ enrollmentId: enr.id, classDate: attendanceDate, status: 'absent' }); toast.success('Marked Absent'); loadData(); } catch (e) { toast.error('Error marking attendance'); setCourseAttendance(prev => ({...prev, [enr.id]: undefined})); }
                            }} className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-600 hover:text-white rounded-lg transition-colors text-xs font-bold flex items-center shadow-sm"><X className="w-3.5 h-3.5 mr-1" /> Absent</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                    <input type="checkbox" checked={courseViewMembers.length > 0 && selectedMembers.length === courseViewMembers.length} onChange={(e) => toggleSelectAll(e, courseViewMembers)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase flex-1">Member</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{(memberFilterStatus === 'completed' || memberFilterStatus === 'dropped') ? 'Date Removed' : 'Set Attendance'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {courseViewMembers.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 bg-gray-50/50">No members found.</td></tr>
                ) : courseViewMembers.map(enr => {
                  const currentStatus = courseAttendance[enr.id];
                  return (
                  <tr key={enr.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-4 py-4 w-12 text-center align-middle">
                      <input type="checkbox" checked={selectedMembers.includes(enr.id)} onChange={() => toggleMemberSelection(enr.id)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                    </td>
                    <td className="px-4 py-4 cursor-pointer group" onClick={() => setSelectedMemberDetails(enr)}>
                      <div className="font-bold text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{enr.name || enr.studentName || 'Member'}</div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">📱 {enr.mobile}</div>
                      {enr.sponsoredBy && <div className="text-[10px] text-gray-400 mt-1 uppercase font-semibold">Sponsor: <span className="text-gray-600">{enr.sponsoredBy}</span></div>}
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className={`px-2 py-1 text-[11px] font-bold uppercase rounded-full tracking-wider ${enr.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' : enr.status === 'active' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>{enr.status || 'active'}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-[11px] font-bold uppercase rounded-full tracking-wider inline-block mb-1.5 ${enr.paymentStatus === 'paid' ? 'bg-green-100 text-green-700 border border-green-200' :
                        enr.paymentStatus === 'partial' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                          'bg-red-100 text-red-700 border border-red-200'
                        }`}>{enr.paymentStatus || 'pending'}</span>
                      <div className="text-xs font-semibold text-gray-600 bg-gray-50 py-1 px-2 rounded-md inline-block">Balance: <span className={parseFloat(enr.balance || 0) > 0 ? "text-red-600" : "text-green-600"}>₹{parseFloat(enr.balance || 0).toLocaleString()}</span></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-4">
                        <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1.5 rounded-lg border border-green-100"><span className="text-[10px] text-green-600 font-semibold uppercase">Attended</span><span className="font-black text-green-700 text-sm">{enr.attendedDays || 0}</span></div>
                        <div className="flex items-center gap-1.5 bg-red-50 px-2 py-1.5 rounded-lg border border-red-100"><span className="text-[10px] text-red-600 font-semibold uppercase">Absent</span><span className="font-black text-red-700 text-sm">{enr.absentDays || 0}</span></div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {enr.status === 'completed' || enr.status === 'dropped' ? (
                        <div className="inline-flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                          <span className="text-sm font-bold text-gray-700">
                            {enr.courseEndDate ? (enr.courseEndDate.seconds ? format(new Date(enr.courseEndDate.seconds * 1000), 'MMM dd, yyyy') : format(new Date(enr.courseEndDate), 'MMM dd, yyyy')) : 'Unknown'}
                          </span>
                        </div>
                      ) : currentStatus ? (
                         <span className={`px-3 py-1.5 text-[11px] font-black uppercase rounded-lg border inline-block min-w-[80px] ${currentStatus === 'present' ? 'bg-green-50 text-green-700 border-green-200 shadow-sm' : 'bg-red-50 text-red-700 border-red-200 shadow-sm'}`}>
                           {currentStatus === 'present' ? 'Attended' : 'Absent'}
                         </span>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={async () => {
                            try { setCourseAttendance(prev => ({...prev, [enr.id]: 'present'})); await markAttendance({ enrollmentId: enr.id, classDate: attendanceDate, status: 'present' }); toast.success('Marked Attended'); loadData(); } catch (e) { toast.error('Error marking attendance'); setCourseAttendance(prev => ({...prev, [enr.id]: undefined})); }
                          }} className="p-2 bg-green-50 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-colors group relative" title="Mark Present"><CheckCircle className="w-5 h-5" /></button>
                          <button onClick={async () => {
                            try { setCourseAttendance(prev => ({...prev, [enr.id]: 'absent'})); await markAttendance({ enrollmentId: enr.id, classDate: attendanceDate, status: 'absent' }); toast.success('Marked Absent'); loadData(); } catch (e) { toast.error('Error marking attendance'); setCourseAttendance(prev => ({...prev, [enr.id]: undefined})); }
                          }} className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors group relative" title="Mark Absent"><X className="w-5 h-5" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'courses' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {courses.map(course => (
            <div key={course.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer group flex flex-col h-full" onClick={() => setSelectedCourseDetails(course)}>
              <div className="p-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white flex-1">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-black text-lg text-gray-900 group-hover:text-blue-700 transition-colors line-clamp-2 md:mr-2">{course.name}</h3>
                  <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded-md shrink-0 border border-blue-200 uppercase tracking-wider">{course.classId || 'Class'}</span>
                </div>
                <p className="text-sm text-gray-500 line-clamp-3 mb-4">{course.description || 'No description provided'}</p>

                <div className="space-y-2 mt-auto">
                  <div className="flex items-center text-xs font-semibold text-gray-600 bg-gray-50 rounded-md p-1.5"><Calendar className="w-3.5 h-3.5 mr-2 text-blue-500" /> {course.startDate ? `${course.startDate} ➔ ${course.endDate || 'Ongoing'}` : 'Dates not set'}</div>
                  <div className="flex items-center text-xs font-semibold text-gray-600 bg-gray-50 rounded-md p-1.5"><Clock className="w-3.5 h-3.5 mr-2 text-purple-500" /> {course.totalHours ? `${course.totalHours} hrs total` : `${course.totalClasses || 0} sessions`}</div>
                </div>
              </div>
              <div className="p-4 bg-white border-t border-gray-50 flex items-center justify-between">
                <div className="flex items-center text-xs font-bold text-gray-700">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2"><Users className="w-3 h-3 text-blue-600" /></div>
                  {enrollments.filter(e => e.courseId === course.id).length} Members Enrolled
                </div>
                <div className="text-blue-600 flex items-center text-xs font-bold group-hover:underline">View <ChevronRight className="w-3 h-3 ml-1" /></div>
              </div>
            </div>
          ))}
          {courses.length === 0 && (
            <div className="col-span-full py-16 text-center text-gray-500 bg-white rounded-2xl border-2 border-dashed border-gray-300">
              <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium text-gray-600">No classes created yet</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Course" above to get started</p>
            </div>
          )}
        </div>
      ) : activeTab === 'members' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8 p-4 md:p-6 animate-fadeIn">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
             <div>
               <h2 className="text-xl font-bold text-gray-900">All Enrolled Members</h2>
               <p className="text-sm text-gray-500 mt-1">Database of all registered members across all classes</p>
             </div>
             <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                   <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                   <input type="text" placeholder="Search members..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                </div>
                <button onClick={() => setShowEnrollmentModal(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-lg text-sm font-semibold whitespace-nowrap shadow-sm"><Plus className="w-4 h-4 inline-block mr-1"/> New Member</button>
             </div>
          </div>
          
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Class Enrolled</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm">
                {filteredEnrollments.length === 0 ? (
                   <tr><td colSpan="4" className="text-center py-8 text-gray-500">No members found</td></tr>
                ) : filteredEnrollments.map(enr => (
                  <tr key={enr.id} onClick={() => setSelectedMemberDetails(enr)} className="hover:bg-blue-50/50 cursor-pointer transition-colors group bg-white">
                     <td className="px-4 py-4 font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {enr.name || enr.studentName || 'Member'}
                     </td>
                     <td className="px-4 py-4 text-gray-600 font-medium">{enr.courseName}</td>
                     <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md tracking-wider ${enr.status === 'completed' ? 'bg-green-100 text-green-700' : enr.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{enr.status || 'active'}</span>
                     </td>
                     <td className="px-4 py-4 text-gray-500">📱 {enr.mobile}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Modals */}
      {showCourseModal && (
        <CourseModal
          initialData={editingItem}
          onClose={() => { setShowCourseModal(false); setEditingItem(null); }}
          onSubmit={editingItem ? handleUpdateCourse : handleAddCourse}
        />
      )}

      {showDeleteModal && deleteItem && (
        <DeleteConfirmModal
          itemName={deleteItem.name}
          onClose={() => { setShowDeleteModal(false); setDeleteItem(null); }}
          onConfirm={handleDeleteCourse}
        />
      )}

      {showEnrollmentModal && (
        <EnrollmentModal
          courses={courses}
          staff={staff}
          enrollments={enrollments}
          users={users || []}
          initialCourseId={selectedCourseDetails ? selectedCourseDetails.id : ''}
          onClose={() => setShowEnrollmentModal(false)}
          onSubmit={async (enrollmentData) => {
            try {
              await addEnrollment(enrollmentData);
              toast.success('Enrollment added successfully');
              loadData();
              setShowEnrollmentModal(false);
            } catch (error) {
              toast.error('Failed to add enrollment');
            }
          }}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          enrollment={selectedEnrollment}
          onClose={() => setShowPaymentModal(false)}
          onSubmit={handleRecordPayment}
        />
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </div>
);

const PaymentModal = ({ enrollment, onClose, onSubmit }) => {
  const [payments, setPayments] = useState([{ amount: '', method: 'cash' }]);
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const validPayments = payments
       .filter(p => p.amount && parseFloat(p.amount) > 0)
       .map(p => ({
         amount: p.amount,
         paymentMethod: p.method,
         notes,
         paymentDate: new Date().toISOString()
       }));
    
    if (validPayments.length === 0) {
       toast.error('Please enter a valid amount');
       return;
    }
    onSubmit(validPayments);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center"><DollarSign className="w-5 h-5 mr-2 text-green-500"/> Record Payment</h3>
          <button onClick={onClose} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"><X className="w-4 h-4 text-gray-600"/></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mb-4">
             <label className="block text-sm font-semibold text-gray-700">Payment Amounts</label>
             {payments.map((payment, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <div className="flex-1">
                    <input type="number" value={payment.amount} onChange={(e) => {
                       const newPayments = [...payments];
                       newPayments[idx].amount = e.target.value;
                       setPayments(newPayments);
                    }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-semibold" min="0" placeholder="0" required />
                  </div>
                  <div className="w-2/5 shrink-0">
                    <select value={payment.method} onChange={(e) => {
                       const newPayments = [...payments];
                       newPayments[idx].method = e.target.value;
                       setPayments(newPayments);
                    }} className="w-full px-2 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-semibold capitalize">
                      <option value="cash">Cash</option>
                      <option value="gpay">GPay</option>
                      <option value="card">Card</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>
                  {idx > 0 && (
                    <button type="button" onClick={() => {
                       setPayments(payments.filter((_, i) => i !== idx));
                    }} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><X className="w-4 h-4"/></button>
                  )}
                </div>
             ))}
             <button type="button" onClick={() => {
                setPayments([...payments, { amount: '', method: 'gpay' }]);
             }} className="text-xs font-bold text-blue-600 self-start hover:underline mt-1 inline-block">+ Add Split Payment</button>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none"
              rows="2"
              placeholder="Optional payment notes..."
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl transition-colors shadow-sm text-sm">
              Record Payments
            </button>
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-4 py-2.5 rounded-xl transition-colors text-sm">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CourseModal = ({ initialData, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(initialData || {
    classId: `CLASS${format(new Date(), 'yyyyMMdd')}`,
    name: '',
    description: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    totalHours: '',
    totalClasses: '',
    courseFee: '',
    meetingLink: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center">
             <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
               <BookOpen className="w-5 h-5 text-blue-600" />
             </div>
             <div>
               <h3 className="text-xl font-bold text-gray-900">{initialData ? 'Edit Class' : 'Add New Class'}</h3>
               <p className="text-xs text-gray-500 mt-0.5">{initialData ? 'Update the details for this class' : 'Fill in the details to create a new class schedule'}</p>
             </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto custom-scrollbar">
          <form id="courseForm" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Info Section */}
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><Edit2 className="w-4 h-4 mr-2 text-blue-500"/> Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Course Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm" placeholder="e.g. Advanced Nutrition Class" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Class ID <span className="text-gray-400 font-normal">(Auto-generated)</span> *</label>
                  <input type="text" value={formData.classId} onChange={(e) => setFormData({...formData, classId: e.target.value})} className="w-full px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-mono text-blue-800" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Course Fee (₹) *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 justify-center text-gray-500 text-sm font-medium">₹</span>
                    <input type="number" value={formData.courseFee} onChange={(e) => setFormData({...formData, courseFee: e.target.value})} className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="0.00" required min="0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Schedule Section */}
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><Calendar className="w-4 h-4 mr-2 text-purple-500"/> Schedule & Duration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Start Date *</label>
                  <CustomDatePicker
                     value={formData.startDate}
                     onChange={(val) => setFormData({...formData, startDate: val, classId: `CLASS${val.replace(/-/g, '')}`})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">End Date</label>
                  <CustomDatePicker
                     value={formData.endDate}
                     onChange={(val) => setFormData({...formData, endDate: val})}
                     minDate={formData.startDate}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Total Classes/Days *</label>
                  <input type="number" value={formData.totalClasses} onChange={(e) => setFormData({...formData, totalClasses: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="e.g. 10" required min="1" />
                </div>
                <div>
                   <label className="block text-xs font-semibold text-gray-700 mb-1">Total Hours <span className="text-gray-400 font-normal">(Optional)</span></label>
                   <input type="number" value={formData.totalHours} onChange={(e) => setFormData({...formData, totalHours: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="e.g. 20" min="0" step="0.5" />
                </div>
              </div>
            </div>

            {/* Additional Info Section */}
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><Video className="w-4 h-4 mr-2 text-green-500"/> Additional Information</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Meeting Link</label>
                  <input type="url" value={formData.meetingLink} onChange={(e) => setFormData({...formData, meetingLink: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="https://meet.google.com/..." />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none" rows="3" placeholder="Enter details about this class..." />
                </div>
              </div>
            </div>

          </form>
        </div>
        
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 z-10 shrink-0">
          <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
          <button type="submit" form="courseForm" className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-sm shadow-blue-200 transition-all active:scale-95 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2" />
            Create Class
          </button>
        </div>
      </div>
    </div>
  );
};

const EnrollmentModal = ({ courses, staff, enrollments = [], users = [], onClose, onSubmit, initialCourseId = '' }) => {
  const initialCourse = courses.find(c => c.id === initialCourseId);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    gender: '',
    address: '',
    sponsoredBy: '',
    courseId: initialCourseId,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    courseEndDate: '',
    paymentDueDate: format(new Date(), 'yyyy-MM-dd'),
    totalAmount: initialCourse?.courseFee || '',
    payments: [{ amount: '', method: 'cash' }]
  });

  const selectedCourse = courses.find(c => c.id === formData.courseId);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const existingMembers = Array.from(new Map(
    enrollments.filter(e => e.name || e.studentName).map(e => [e.mobile, {
      name: e.name || e.studentName || '',
      mobile: e.mobile || '',
      gender: e.gender || '',
      address: e.address || ''
    }])
  ).values());
  const suggestions = existingMembers.filter(m => m.name.toLowerCase().includes(formData.name.toLowerCase()) && m.name.toLowerCase() !== formData.name.toLowerCase());

  const [showSponsoredSuggestions, setShowSponsoredSuggestions] = useState(false);
  const getFilteredUsers = (term) => users.filter(u => (u.name || u.personalInformation?.fullName || '').toLowerCase().includes((term || '').toLowerCase()));

  const handleSubmit = (e) => {
    e.preventDefault();
    const totalPaidAmount = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const balance = parseFloat(formData.totalAmount || selectedCourse?.courseFee || '0') - totalPaidAmount;
    const totalAmt = parseFloat(formData.totalAmount || selectedCourse?.courseFee || '0');
    let paymentStatus = 'pending';
    if (totalPaidAmount >= totalAmt && totalAmt > 0) paymentStatus = 'paid';
    else if (totalPaidAmount > 0) paymentStatus = 'partial';

    const validPayments = formData.payments.filter(p => p.amount && parseFloat(p.amount) > 0);
    const paymentHistory = validPayments.map(p => ({
        amount: p.amount,
        paymentMethod: p.method,
        paymentDate: new Date().toISOString(),
        notes: 'Initial payment',
        recordedAt: new Date().toISOString()
    }));

    const enrollmentData = {
      name: formData.name,
      mobile: formData.mobile,
      gender: formData.gender,
      address: formData.address,
      sponsoredBy: formData.sponsoredBy,
      courseId: formData.courseId,
      courseName: selectedCourse?.name || '',
      totalClasses: parseInt(selectedCourse?.totalClasses) || 0,
      startDate: formData.startDate,
      courseEndDate: formData.courseEndDate,
      totalAmount: formData.totalAmount || selectedCourse?.courseFee || '0',
      amountPaid: totalPaidAmount.toString(),
      balance: balance.toString(),
      paymentStatus,
      paymentDueDate: formData.paymentDueDate,
      paymentHistory
    };
    onSubmit(enrollmentData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0">
          <div className="flex items-center">
             <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
               <User className="w-5 h-5 text-blue-600" />
             </div>
             <div>
               <h3 className="text-xl font-bold text-gray-900">New Enrollment</h3>
               <p className="text-xs text-gray-500 mt-0.5">Register a member to a class and record initial payment.</p>
             </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar">
          <form id="enrollmentForm" onSubmit={handleSubmit} className="space-y-6">
             {/* Student Information */}
             <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><User className="w-4 h-4 mr-2 text-blue-500"/> Member Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Sponsored By</label>
                    <input type="text" value={formData.sponsoredBy} onFocus={() => setShowSponsoredSuggestions(true)} onBlur={() => setTimeout(() => setShowSponsoredSuggestions(false), 200)} onChange={(e) => { setFormData({...formData, sponsoredBy: e.target.value}); setShowSponsoredSuggestions(true); }} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="Search or enter sponsor's name..." autoComplete="off" />
                    {showSponsoredSuggestions && getFilteredUsers(formData.sponsoredBy).length > 0 && formData.sponsoredBy?.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto custom-scrollbar">
                        {getFilteredUsers(formData.sponsoredBy).map((user, idx) => (
                          <div key={idx} className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0" onClick={() => {
                            setFormData({...formData, sponsoredBy: user.name || user.personalInformation?.fullName});
                            setShowSponsoredSuggestions(false);
                          }}>
                            <div className="font-bold text-sm text-gray-800">{user.name || user.personalInformation?.fullName}</div>
                            <div className="text-xs text-gray-500">{user.mobile || user.contactDetails?.mobile || ''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                    <input type="text" value={formData.name} onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} onChange={(e) => { setFormData({...formData, name: e.target.value}); setShowSuggestions(true); }} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="e.g. John Doe" required autoComplete="off" />
                    
                    {showSuggestions && suggestions.length > 0 && formData.name.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto custom-scrollbar">
                        {suggestions.map((member, idx) => (
                          <div key={idx} className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0" onClick={() => {
                            setFormData({...formData, name: member.name, mobile: member.mobile, gender: member.gender, address: member.address || ''});
                            setShowSuggestions(false);
                          }}>
                            <div className="font-bold text-sm text-gray-800">{member.name}</div>
                            <div className="text-xs text-gray-500 flex gap-3 mt-0.5"><span>📱 {member.mobile}</span> <span className="capitalize">👤 {member.gender}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Number *</label>
                    <input type="tel" value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" required pattern="[0-9]{10}" placeholder="10-digit number" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Gender *</label>
                    <select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" required>
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Address</label>
                    <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder="Optional" />
                  </div>
                </div>
             </div>

             {/* Course Details */}
             <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><BookOpen className="w-4 h-4 mr-2 text-purple-500"/> Course Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Select Course *</label>
                    <select disabled={!!initialCourseId} value={formData.courseId} onChange={(e) => {
                         const c = courses.find(x => x.id === e.target.value);
                         setFormData({...formData, courseId: e.target.value, totalAmount: c?.courseFee || ''});
                    }} className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm disabled:opacity-60 disabled:bg-gray-100" required>
                      <option value="">Choose a course...</option>
                      {courses.map(c => (<option key={c.id} value={c.id}>{c.name} - ₹{c.courseFee} ({c.totalClasses} classes)</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Enrollment Date *</label>
                    <CustomDatePicker value={formData.startDate} onChange={(val) => setFormData({...formData, startDate: val})} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Estimated End Date</label>
                    <CustomDatePicker value={formData.courseEndDate} onChange={(val) => setFormData({...formData, courseEndDate: val})} minDate={formData.startDate} />
                  </div>
                </div>
             </div>

             {/* Payment Information */}
             <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center"><DollarSign className="w-4 h-4 mr-2 text-green-500"/> Fee & Payment</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Total Fee (₹) *</label>
                    <input type="number" value={formData.totalAmount} onChange={(e) => setFormData({...formData, totalAmount: e.target.value})} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-semibold" required min="0" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Amount Paying Now (₹)</label>
                    {formData.payments.map((payment, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <div className="flex-1">
                          <input type="number" value={payment.amount} onChange={(e) => {
                             const newPayments = [...formData.payments];
                             newPayments[idx].amount = e.target.value;
                             setFormData({...formData, payments: newPayments});
                          }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm text-green-700 font-semibold" min="0" placeholder="0" />
                        </div>
                        <div className="w-2/5 shrink-0">
                          <select value={payment.method} onChange={(e) => {
                             const newPayments = [...formData.payments];
                             newPayments[idx].method = e.target.value;
                             setFormData({...formData, payments: newPayments});
                          }} className="w-full px-2 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm font-semibold capitalize">
                            <option value="cash">Cash</option>
                            <option value="gpay">GPay</option>
                            <option value="card">Card</option>
                            <option value="transfer">Transfer</option>
                          </select>
                        </div>
                        {idx > 0 && (
                          <button type="button" onClick={() => {
                             const newPayments = formData.payments.filter((_, i) => i !== idx);
                             setFormData({...formData, payments: newPayments});
                          }} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><X className="w-4 h-4"/></button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => {
                       setFormData({...formData, payments: [...formData.payments, { amount: '', method: 'gpay' }]});
                    }} className="text-[10px] font-bold text-blue-600 self-start hover:underline mt-1">+ Add Split Payment</button>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Remaining Balance</label>
                    <div className="w-full px-4 py-2.5 bg-gray-200 border border-gray-300 rounded-xl text-sm font-bold text-red-600 flex items-center h-[42px]">
                      ₹{Math.max(0, (parseFloat(formData.totalAmount || selectedCourse?.courseFee || '0') - formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Next Payment Due</label>
                    <CustomDatePicker value={formData.paymentDueDate} onChange={(val) => setFormData({...formData, paymentDueDate: val})} minDate={formData.startDate} />
                  </div>
                </div>
             </div>

          </form>
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 z-10 shrink-0">
          <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
          <button type="submit" form="enrollmentForm" className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-sm shadow-blue-200 transition-all active:scale-95 flex items-center">
             Enroll Member
             <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ itemName, onClose, onConfirm }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
    <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-2xl">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-red-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Course?</h3>
      <p className="text-sm text-gray-500 mb-6">Are you sure you want to delete <span className="font-bold">{itemName}</span>? This action cannot be undone.</p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors">Cancel</button>
        <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-sm">Delete</button>
      </div>
    </div>
  </div>
);

export default ClassManagement;